// Colours river polygon features by the alert level of the nearest gauge.
//
// LINZ layer 103631 (NZ River Name Polygons) supplies one Polygon feature per
// named river section. Each polygon already has the correct river width baked
// into its geometry, so no line-width tricks are needed.
//
// Matching strategy (in order):
//   1. Name match — derive the river name from the gauge site name by taking
//      everything before " at " (e.g. "Wairoa River at Town Bridge" →
//      "Wairoa River"), then normalize both sides by stripping macrons and
//      lowercasing. Assigns each polygon to the spatially nearest same-name
//      gauge within NAME_MAX_DIST degrees. This handles long rivers with a
//      single large polygon (whose centroid may be far from any gauge) and
//      HBRC/LINZ name variants that differ only in macrons (e.g. "Pōrangahau"
//      vs "Porangahau").
//   2. Distance fallback — for polygons with no name-matched gauge, assign the
//      nearest gauge within DIST_MAX degrees. Picks up small streams whose
//      gauge name doesn't derive cleanly to the polygon name.
//
// Polygons with no match get alert_level null (rendered as steel-blue).

import type { GaugeFeatureCollection, AlertLevel } from "../api.ts";

type Coord = [number, number]; // [lon, lat]

// Generous limit for name-matched gauges — long rivers can have centroids
// many km from the gauge, but the name match confirms it's the right river.
const NAME_MAX_DIST = 0.3; // ~33 km

// Tight limit for the distance-only fallback — avoids false cross-river matches.
const DIST_MAX = 0.05; // ~5.5 km

// Strip combining diacritics (macrons etc.) and lowercase.
function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

// "Wairoa River at Town Bridge" → "wairoa river"
// "Karamu Stream at Floodgates" → "karamu stream"
function deriveRiverName(siteName: string): string {
  const i = siteName.indexOf(" at ");
  return normalize(i >= 0 ? siteName.slice(0, i) : siteName);
}

function centroid(ring: Coord[]): Coord {
  let x = 0;
  let y = 0;
  for (const [cx, cy] of ring) {
    x += cx;
    y += cy;
  }
  return [x / ring.length, y / ring.length];
}

function dist2(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) ** 2 + (ay - by) ** 2;
}

// Assign alert_level to each river polygon based on the nearest gauge.
// Returns a new FeatureCollection with alert_level added to properties.
export function colorRiverPolygons(
  riverPolygons: GeoJSON.FeatureCollection,
  riverGauges: GaugeFeatureCollection,
): GeoJSON.FeatureCollection {
  type Gauge = { lon: number; lat: number; alertLevel: AlertLevel | null; riverName: string };

  const gauges: Gauge[] = riverGauges.features.map((f) => ({
    lon: f.geometry.coordinates[0] ?? 0,
    lat: f.geometry.coordinates[1] ?? 0,
    alertLevel: f.properties.alert_level,
    riverName: deriveRiverName(f.properties.site_name),
  }));

  // Index gauges by derived river name for fast lookup.
  const byRiverName = new Map<string, Gauge[]>();
  for (const g of gauges) {
    const list = byRiverName.get(g.riverName);
    if (list) list.push(g);
    else byRiverName.set(g.riverName, [g]);
  }

  const nameMaxDist2 = NAME_MAX_DIST * NAME_MAX_DIST;
  const distMax2 = DIST_MAX * DIST_MAX;

  const features = riverPolygons.features.map((feature) => {
    if (feature.geometry.type !== "Polygon") {
      return { ...feature, properties: { ...feature.properties, alert_level: null } };
    }

    const ring = feature.geometry.coordinates[0] as Coord[];
    const [cx, cy] = centroid(ring);
    const polyName = normalize(feature.properties?.name ?? "");

    let alertLevel: AlertLevel | null = null;
    let bestDist2 = Infinity;

    // 1. Name match: find nearest same-river gauge within NAME_MAX_DIST.
    const namedCandidates = byRiverName.get(polyName);
    if (namedCandidates) {
      for (const g of namedCandidates) {
        const d2 = dist2(cx, cy, g.lon, g.lat);
        if (d2 < bestDist2 && d2 < nameMaxDist2) {
          bestDist2 = d2;
          alertLevel = g.alertLevel;
        }
      }
    }

    // 2. Distance fallback: nearest gauge of any name within DIST_MAX.
    if (alertLevel === null) {
      bestDist2 = Infinity;
      for (const g of gauges) {
        const d2 = dist2(cx, cy, g.lon, g.lat);
        if (d2 < bestDist2 && d2 < distMax2) {
          bestDist2 = d2;
          alertLevel = g.alertLevel;
        }
      }
    }

    return {
      ...feature,
      properties: { ...feature.properties, alert_level: alertLevel },
    };
  });

  return { type: "FeatureCollection", features };
}
